import React from 'react';
import { Row, Col, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import UspStrip from '../components/UspStrip';
import Footer from '../components/Footer';
import AntigravityAnimation from '../components/AntigravityAnimation';
import konicaMinoltaImg from '../assets/konica-minolta.png';
import visitingCardsImg from '../assets/Visiting-Cards.jpg';
import flyersBrochuresImg from '../assets/Flyers-Brochures.jpg';
import weddingInvitationsImg from '../assets/Wedding-Invitations.jpg';
import flexPrintingImg from '../assets/flex-printing.jpg';
import bannerImg from '../assets/printing_hero_banner.png';
import photoFramesImg from '../assets/photo-frames.png';

interface HomePageProps {
  onGetQuote: () => void;
}

const services = [
  { img: visitingCardsImg, title: 'Visiting Cards', desc: 'Premium quality visiting cards with various finishes.', portfolioCategory: 'Visiting Cards' },
  { img: flyersBrochuresImg, title: 'Flyers', desc: 'Eye-catching flyers for your promotions.', portfolioCategory: 'Flyers' },
  { img: flyersBrochuresImg, title: 'Brochures', desc: 'High-quality catalog pamphlets and menu folds.', portfolioCategory: 'Flyers' },
  { img: weddingInvitationsImg, title: 'Wedding Invitations', desc: 'Elegant wedding invitation cards to make your moments special.', portfolioCategory: 'Wedding Invites' },
  { img: flexPrintingImg, title: 'Flex Printing', desc: 'High quality flex printing for indoor and outdoor use.', portfolioCategory: 'Banners' },
  { img: photoFramesImg, title: 'Photo Frames', desc: 'Premium custom photo frames for your cherished memories.', portfolioCategory: 'Stickers & Labels' },
];

const detailedServices = [
  {
    icon: 'fas fa-print',
    title: 'High-Speed Digital Press',
    desc: 'Short-run, high-precision print production on industry-leading laser presses. Perfect for brochures, flyers, booklets, and custom cards.'
  },
  {
    icon: 'fas fa-image',
    title: 'Large Format Flex & Signage',
    desc: 'Vibrant large-scale outdoor signage, billboards, lightboxes, backlit banners, and vinyl event boards designed to make a massive visual statement.'
  },
  {
    icon: 'fas fa-envelope-open-text',
    title: 'Stationery & Custom Invites',
    desc: 'Luxury cards, envelope sets, illustrations, and premium invitations designed with special foil layers and textures for unforgettable occasions.'
  },
  {
    icon: 'fas fa-id-card',
    title: 'Corporate Branding & Merchandise',
    desc: 'Elevate your corporate brand identity with custom plastic employee ID badges, access smartcards, logo lanyards, custom plaques, and photo frames.'
  },
  {
    icon: 'fas fa-paint-brush',
    title: 'Bespoke Designing & Prepress',
    desc: 'Creative design studio services to craft logos, layout templates, typography schemas, and files fully print-ready with accurate bleed margins.'
  },
  {
    icon: 'fas fa-book-open',
    title: 'Post-Press & Binding Services',
    desc: 'Provide clean professional finishes to text documents, newsletters, brochures, and menu lists using high-quality laminations, folds, and bindings.'
  }
];

const stats = [
  { icon: 'fa-users', num: '100+', desc: 'Happy Customers' },
  { icon: 'fa-folder-open', num: '80+', desc: 'Projects Completed' },
  { icon: 'fa-award', num: '2+', desc: 'Years of Experience' },
];

const features = [
  { icon: 'fa-bolt', title: 'Fast', sub: 'Turnaround' },
  { icon: 'fa-tag', title: 'Affordable', sub: 'Pricing' },
  { icon: 'fa-smile', title: 'Customer', sub: 'Satisfaction' },
];

const processSteps = [
  { step: '1', icon: 'fa-sliders-h', title: 'Configure Specifications', desc: 'Pick your product, choose quantities, sizes, and card stock finish.' },
  { step: '2', icon: 'fa-file-invoice-dollar', title: 'Get Instant Estimate', desc: 'View live pricing calculated instantly for your custom configurations.' },
  { step: '3', icon: 'fa-paper-plane', title: 'Approve & Print', desc: 'Submit order details directly to our WhatsApp support team for fast printing.' },
];

const HomePage: React.FC<HomePageProps> = ({ onGetQuote }) => {
  const navigate = useNavigate();

  return (
    <div className="page-fade d-flex flex-column" style={{ minHeight: 'calc(100vh - 68px)' }}>
      <div style={{ flex: 1 }}>

        {/* HERO */}
        <div className="hero-section d-flex align-items-center justify-content-end flex-wrap gap-4" style={{ minHeight: '520px', position: 'relative' }}>
          {/* Background Image filling the entire hero section */}
          <img
            src={bannerImg}
            alt="Hero Background"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.7,
              zIndex: 0
            }}
          />

          {/* Antigravity Interactive Particles Animation */}
          <AntigravityAnimation />

          <div className="hero-content hero-glass-card hero-animate-right" style={{ maxWidth: '640px', position: 'relative', zIndex: 1, flex: '1 1 auto', minWidth: '280px' }}>
            <h1 style={{ letterSpacing: '-1px', fontSize: 'clamp(28px, 6vw, 48px)' }}>Premium <span>Digital Printing</span> Services</h1>
            <p style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', lineHeight: 1.6 }}>High-quality printing solutions for businesses and individuals. From premium visiting cards to durable flex banners, we deliver precision and excellence every time.</p>
            <div className="d-flex gap-3 flex-wrap mb-4">
              <button className="btn-primary-custom" onClick={() => navigate('/products')}>
                <i className="fas fa-search me-1"></i> Explore Products
              </button>
              <button className="btn-secondary-custom" onClick={onGetQuote}>
                <i className="fas fa-file-invoice me-1"></i> Get a Quote
              </button>
            </div>
            <div className="d-flex gap-4 flex-wrap">
              {features.map(f => (
                <div className="feature-badge" key={f.title}>
                  <div className="icon"><i className={`fas ${f.icon}`}></i></div>
                  <div className="label">{f.title}<span>{f.sub}</span></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* STATS */}
        <div className="stats-strip d-flex flex-wrap align-items-center justify-content-around gap-4">
          {stats.map((s, i) => (
            <React.Fragment key={s.desc}>
              <div className="d-flex align-items-center gap-3 stat-card-animated">
                <div className="stat-icon" style={{ borderColor: 'rgba(233,30,140,0.15)', color: 'var(--pink)', background: '#fff0f8' }}>
                  <i className={`fas ${s.icon}`}></i>
                </div>
                <div>
                  <div className="stat-num" style={{ fontSize: '24px' }}>{s.num}</div>
                  <div className="stat-desc" style={{ fontWeight: 500 }}>{s.desc}</div>
                </div>
              </div>
              {i < stats.length - 1 && <div className="stat-divider d-none d-md-block"></div>}
            </React.Fragment>
          ))}
        </div>

        {/* SERVICES */}
        <section style={{ padding: 'clamp(40px, 8vw, 80px) 0', background: 'var(--bg-secondary)', overflow: 'hidden', position: 'relative' }}>
          {/* Decorative background elements */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '-80px',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(233, 30, 140, 0.05)',
            zIndex: 0,
            pointerEvents: 'none'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '100px',
            right: '-100px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(59, 91, 219, 0.05)',
            zIndex: 0,
            pointerEvents: 'none'
          }}></div>

          <div className="section-title text-center mb-5" style={{ padding: '0 5%', position: 'relative', zIndex: 1 }}>
            <h2 style={{
              fontSize: 'clamp(28px, 6vw, 42px)',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              marginBottom: '20px',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #3b5bdb 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Our <span style={{ background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Popular</span> Printing Services</h2>
            <div className="section-underline" style={{
              width: '70px',
              height: '4px',
              background: 'linear-gradient(90deg, #e91e8c 0%, #3b5bdb 100%)',
              borderRadius: '2px',
              boxShadow: '0 4px 12px rgba(233, 30, 140, 0.3)',
              margin: '0 auto'
            }}></div>
          </div>

          <div className="marquee-container">
            <div className="marquee-track">
              {/* First Set */}
              {services.map((s, idx) => (
                <div
                  className="service-card marquee-card"
                  key={`s1-${idx}`}
                  style={{ cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                  onClick={() => navigate('/portfolio', { state: { category: s.portfolioCategory } })}
                >
                  <div className="service-img-wrap">
                    <img src={s.img} alt={s.title} />
                  </div>
                  <div className="card-body p-4 text-center">
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--dark)', marginBottom: '8px' }}>{s.title}</h3>
                    <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.6', marginBottom: '8px' }}>{s.desc}</p>
                    <div className="text-primary fw-semibold small d-flex align-items-center gap-1 justify-content-center">
                      <span>View Our Work</span>
                      <i className="fas fa-arrow-right" style={{ fontSize: '10px' }}></i>
                    </div>
                  </div>
                </div>
              ))}
              {/* Second Set (Duplicate for seamless loop) */}
              {services.map((s, idx) => (
                <div
                  className="service-card marquee-card"
                  key={`s2-${idx}`}
                  style={{ cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                  onClick={() => navigate('/portfolio', { state: { category: s.portfolioCategory } })}
                >
                  <div className="service-img-wrap">
                    <img src={s.img} alt={s.title} />
                  </div>
                  <div className="card-body p-4 text-center">
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--dark)', marginBottom: '8px' }}>{s.title}</h3>
                    <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.6', marginBottom: '8px' }}>{s.desc}</p>
                    <div className="text-primary fw-semibold small d-flex align-items-center gap-1 justify-content-center">
                      <span>View Our Work</span>
                      <i className="fas fa-arrow-right" style={{ fontSize: '10px' }}></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-5" style={{ padding: '0 5%', position: 'relative', zIndex: 1 }}>
            <button
              className="btn-outline-custom"
              onClick={() => navigate('/products')}
              style={{
                padding: '14px 32px',
                borderRadius: '10px',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                color: 'white',
                border: 'none',
                fontSize: 'clamp(13px, 2.5vw, 15px)',
                boxShadow: '0 6px 20px rgba(233, 30, 140, 0.35)',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                cursor: 'pointer',
                letterSpacing: '0.3px'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 28px rgba(233, 30, 140, 0.45)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(233, 30, 140, 0.35)';
              }}
            >
              View All Products <i className="fas fa-arrow-right ms-1"></i>
            </button>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section style={{ padding: 'clamp(40px, 8vw, 80px) 5%', background: 'var(--bg-primary)', position: 'relative', overflow: 'hidden' }}>
          {/* Decorative background elements */}
          <div style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(233, 30, 140, 0.05)',
            zIndex: 0,
            pointerEvents: 'none'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '-80px',
            left: '-80px',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(59, 91, 219, 0.05)',
            zIndex: 0,
            pointerEvents: 'none'
          }}></div>

          <Container style={{ position: 'relative', zIndex: 1 }}>
            <div className="section-title text-center mb-5">
              <h2 style={{
                fontSize: 'clamp(28px, 6vw, 42px)',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                marginBottom: '20px',
                background: 'linear-gradient(135deg, #1a1a1a 0%, #3b5bdb 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>How It <span style={{ background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Works</span></h2>
              <div className="section-underline" style={{
                width: '70px',
                height: '4px',
                background: 'linear-gradient(90deg, #e91e8c 0%, #3b5bdb 100%)',
                borderRadius: '2px',
                boxShadow: '0 4px 12px rgba(233, 30, 140, 0.3)',
                margin: '0 auto 24px'
              }}></div>
              <p className="text-muted mt-3 mx-auto" style={{ maxWidth: '580px', fontSize: 'clamp(14px, 2.5vw, 16px)', lineHeight: '1.7', color: '#555555', fontWeight: 500 }}>
                We make configuring and ordering custom digital printing projects fast, seamless, and completely transparent.
              </p>
            </div>

            <Row className="g-4">
              {processSteps.map((step, idx) => (
                <Col key={step.title} xs={12} md={4}>
                  <div className="process-step-card">
                    <div className="process-step-num">{step.step}</div>
                    <h4 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--dark)', marginBottom: '10px' }}>
                      {step.title}
                    </h4>
                    <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.6', margin: 0 }}>
                      {step.desc}
                    </p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* DETAILED SERVICES SECTION */}
        <section style={{ padding: 'clamp(40px, 8vw, 80px) 5%', background: 'linear-gradient(135deg, #fff5fb 0%, #f8fbff 50%, #ffffff 100%)', position: 'relative', overflow: 'hidden' }}>
          {/* Decorative background elements */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '-80px',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(233, 30, 140, 0.05)',
            zIndex: 0,
            pointerEvents: 'none'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '100px',
            right: '-100px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(59, 91, 219, 0.05)',
            zIndex: 0,
            pointerEvents: 'none'
          }}></div>

          <Container style={{ position: 'relative', zIndex: 1 }}>
            <div className="section-title text-center mb-5">
              <h2 style={{
                fontSize: 'clamp(28px, 6vw, 42px)',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                marginBottom: '20px',
                background: 'linear-gradient(135deg, #1a1a1a 0%, #3b5bdb 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Our <span style={{ background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Complete Services</span></h2>
              <div className="section-underline" style={{
                width: '70px',
                height: '4px',
                background: 'linear-gradient(90deg, #e91e8c 0%, #3b5bdb 100%)',
                borderRadius: '2px',
                boxShadow: '0 4px 12px rgba(233, 30, 140, 0.3)',
                margin: '0 auto 24px'
              }}></div>
              <p className="text-muted mx-auto" style={{ maxWidth: '680px', fontSize: 'clamp(14px, 2.5vw, 16px)', lineHeight: '1.7', fontWeight: 500 }}>
                From digital printing to large format signage, custom invitations to corporate merchandise, we offer comprehensive solutions for all your printing needs.
              </p>
            </div>

            <Row className="g-3 g-lg-4">
              {detailedServices.map((service, idx) => (
                <Col key={idx} xs={12} sm={6} lg={4}>
                  <div
                    className="service-detail-card"
                    style={{
                      background: 'white',
                      border: '1px solid #f0dce8',
                      borderRadius: '14px',
                      padding: 'clamp(16px, 4vw, 32px)',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.transform = 'translateY(-8px)';
                      (e.currentTarget as HTMLElement).style.boxShadow = '0 15px 40px rgba(233, 30, 140, 0.15)';
                      (e.currentTarget as HTMLElement).style.borderColor = '#ffe6f0';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                      (e.currentTarget as HTMLElement).style.borderColor = '#f0dce8';
                    }}
                  >
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '24px',
                      marginBottom: '16px',
                      boxShadow: '0 4px 12px rgba(233, 30, 140, 0.3)',
                    }}>
                      <i className={service.icon}></i>
                    </div>
                    <h4 style={{
                      fontSize: 'clamp(14px, 2vw, 16px)',
                      fontWeight: 700,
                      color: '#1a1a1a',
                      marginBottom: '12px',
                    }}>
                      {service.title}
                    </h4>
                    <p style={{
                      fontSize: 'clamp(12px, 1.8vw, 13.5px)',
                      color: '#666',
                      lineHeight: '1.7',
                      margin: 0,
                      flex: 1,
                    }}>
                      {service.desc}
                    </p>
                    <div style={{
                      marginTop: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#e91e8c',
                      fontWeight: 600,
                      fontSize: '12.5px',
                    }}>
                      <span>Learn More</span>
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
        <div className="home-cta-banner text-center text-md-start" style={{
          background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
          padding: '60px 5%',
          borderRadius: '20px',
          margin: '60px 5%',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(233, 30, 140, 0.35)',
          zIndex: 1
        }}>
          <Row className="align-items-center g-4">
            <Col md={8}>
              <h2 className="fw-bold mb-2" style={{
                fontSize: 'clamp(24px, 5vw, 36px)',
                color: 'white',
                letterSpacing: '-0.5px',
                fontWeight: 700
              }}>Ready to bring your print project to life?</h2>
              <p className="mb-0 opacity-90" style={{
                fontSize: 'clamp(14px, 2.5vw, 16px)',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.6',
                fontWeight: 500
              }}>
                Configure your specifications, get instant pricing, and connect with our support team to lock in high-quality outputs.
              </p>
            </Col>
            <Col md={4} className="text-md-end">
              <div className="d-flex flex-column flex-sm-row justify-content-md-end gap-3">
                <button
                  className="btn-primary-custom"
                  onClick={onGetQuote}
                  style={{
                    background: 'rgba(255,255,255,0.95)',
                    color: '#e91e8c',
                    padding: '14px 30px',
                    borderRadius: '10px',
                    fontSize: 'clamp(13px, 2.5vw, 15px)',
                    fontWeight: 700,
                    border: 'none',
                    boxShadow: '0 4px 15px rgba(255,255,255,0.2)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    letterSpacing: '0.3px'
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(255,255,255,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 15px rgba(255,255,255,0.2)';
                  }}
                >
                  <i className="fas fa-file-invoice me-2"></i> Get Instant Quote
                </button>
                <button
                  className="btn-secondary-custom"
                  onClick={() => navigate('/portfolio')}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    border: '2px solid rgba(255,255,255,0.3)',
                    padding: '12px 30px',
                    borderRadius: '10px',
                    fontSize: 'clamp(13px, 2.5vw, 15px)',
                    fontWeight: 700,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    letterSpacing: '0.3px'
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.25)';
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.15)';
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                  }}
                >
                  <i className="fas fa-images me-2"></i> View Portfolio
                </button>
              </div>
            </Col>
          </Row>
        </div>

      </div>

      <UspStrip />
      <Footer />
    </div>
  );
};

export default HomePage;
